import { AlertTriangle, ArrowUpRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../../i18n/LanguageContext";
import { FOOTER_LEGAL, legal } from "../../i18n/legal";

export const FooterSection = () => {
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const L = legal[lang];

  const go = (id) => {
    if (pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const linkIds = ["markets", "regulation", "italy", "services", "contact"];

  return (
    <footer data-testid="footer-section" className="bg-slate-900 text-slate-400">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div className="border border-amber-700/40 bg-amber-950/20 p-6 lg:p-8 flex gap-4" data-testid="risk-warning">
          <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs lg:text-sm leading-relaxed text-amber-100/80">{t.footer.risk}</p>
        </div>

        <div className="mt-14 grid md:grid-cols-[1.6fr_1fr_1fr] gap-10">
          <div>
            <span className="font-serif text-2xl font-bold text-white">ASTEO</span>
            <p className="mt-4 text-xs leading-relaxed max-w-xl text-slate-500" data-testid="footer-regulatory-notice">{t.footer.reg}</p>
            <a
              data-testid="footer-client-area"
              href="https://client.asteo.eu/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors duration-300"
            >
              {t.nav.portal} <ArrowUpRight size={14} />
            </a>
          </div>

          <nav className="flex flex-col gap-3">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600">{t.footer.siteTitle}</span>
            {linkIds.map((id) => (
              <button key={id} data-testid={`footer-link-${id}`} onClick={() => go(id)} className="text-sm text-slate-400 hover:text-white transition-colors duration-300 text-left">
                {t.footer.links[id]}
              </button>
            ))}
          </nav>

          <nav className="flex flex-col gap-3">
            <Link
              to="/legal"
              data-testid="footer-legal-library"
              className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-400 transition-colors duration-300"
            >
              {t.footer.legalTitle}
            </Link>
            {FOOTER_LEGAL.map((slug) => (
              <Link
                key={slug}
                data-testid={`footer-legal-${slug}`}
                to={`/legal/${slug}`}
                className="text-sm text-slate-400 hover:text-white transition-colors duration-300"
              >
                {L.docs[slug]?.title}
              </Link>
            ))}
          </nav>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row justify-between gap-2 text-xs font-mono text-slate-600">
          <span>© {new Date().getFullYear()} ASTEO LUXEMBOURG S.A. — {t.footer.rights}</span>
          <span>120 Boulevard de la Pétrusse, L-2330 Luxembourg</span>
        </div>
      </div>
    </footer>
  );
};
